import { useEffect } from "react";
import { site, experience, certifications, heroStats } from "../data/content";
import { flagshipProjects } from "../data/projects";
import { Printer, ArrowLeft } from "lucide-react";
import Brand from "../components/Brand";

// Printable one-page CV. Hides nav/footer and uses window.print() for PDF export.
export default function CV() {
  useEffect(() => {
    document.body.classList.add("cv-mode");
    return () => document.body.classList.remove("cv-mode");
  }, []);

  const topFlagships = flagshipProjects.slice(0, 4);

  return (
    <div className="cv-page pt-10 md:pt-14 pb-10 max-w-4xl mx-auto px-6 md:px-10" style={{ color: "var(--text-primary)" }} data-testid="cv-page">
      <div className="flex items-start justify-between gap-4 mb-8 cv-hide-on-print">
        <a href="/" className="inline-flex items-center gap-2 text-sm link-underline" style={{ color: "var(--primary)" }}>
          <ArrowLeft size={14} /> Back to site
        </a>
        <button
          data-testid="cv-print-btn"
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm"
          style={{ backgroundColor: "var(--primary)", color: "var(--bg)" }}
        >
          <Printer size={14} /> Download as PDF (print)
        </button>
      </div>

      <header className="mb-8 pb-6" style={{ borderBottom: "1px solid var(--border-soft)" }}>
        <div className="flex items-center gap-3 mb-4">
          <img src="/logo.png" alt="" className="h-8 w-8 object-contain" />
          <Brand size="md" />
        </div>
        <h1 className="font-serif text-4xl md:text-5xl leading-tight" style={{ color: "var(--primary)" }}>
          {site.name}
        </h1>
        <div className="mt-2 text-base" style={{ color: "var(--text-secondary)" }}>
          {site.tagline} · Renewable Energy · Hybrid PV, Wind & Storage
        </div>
        <div className="mt-3 text-xs font-mono" style={{ color: "var(--text-secondary)" }}>
          {site.email} · {site.phone} · reconsultx.com · LinkedIn: himansshu-agrawal-renewables
        </div>
      </header>

      <section className="mb-8">
        <div className="overline mb-2">Summary</div>
        <p className="text-sm md:text-base leading-relaxed" style={{ color: "var(--text-primary)" }}>
          Award-winning decarbonization expert with 15+ years of experience delivering utility-scale renewable energy, energy storage, and hybrid power systems across <strong>15 countries</strong>. Led the world&apos;s first time-block renewable product for industrial decarbonization. Founder of REConsultX; ex-Serentica Renewables (VP), ex-SunEdison (APAC fleet, Japan D&amp;E, Malaysia design).
        </p>
      </section>

      <section className="mb-8">
        <div className="overline mb-3">Portfolio Highlights</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {heroStats.map((s) => (
            <div key={s.label} className="p-3" style={{ border: "1px solid var(--border-soft)" }}>
              <div className="font-serif text-2xl leading-none" style={{ color: "var(--primary)" }}>
                {s.prefix || ""}{s.value}{s.unit && <span style={{ color: "var(--accent-strong)", fontSize: "0.7em", marginLeft: 2 }}>{s.unit}</span>}
              </div>
              <div className="mt-1 text-[10px] font-mono uppercase tracking-widest" style={{ color: "var(--text-secondary)" }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="mb-8">
        <div className="overline mb-3">Selected Experience</div>
        <ul className="space-y-3">
          {experience.slice(0, 5).map((e, i) => (
            <li key={i} className="grid grid-cols-12 gap-3">
              <div className="col-span-4 font-mono text-xs uppercase tracking-widest pt-0.5" style={{ color: "var(--text-secondary)" }}>
                {e.period}
              </div>
              <div className="col-span-8">
                <div className="text-sm" style={{ color: "var(--primary)" }}>
                  <strong className="font-serif text-base">{e.role}</strong> — <span style={{ color: "var(--accent-strong)" }}>{e.company}</span>
                </div>
                <div className="text-[11px] font-mono uppercase tracking-wide mt-0.5" style={{ color: "var(--text-secondary)" }}>{e.location}</div>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="mb-8">
        <div className="overline mb-3">Signature Projects</div>
        <ul className="space-y-2 text-sm">
          {topFlagships.map((p) => (
            <li key={p.id} className="flex gap-3">
              <span className="font-mono text-[10px] uppercase tracking-widest pt-1 shrink-0 w-16" style={{ color: "var(--accent-strong)" }}>{p.year}</span>
              <span style={{ color: "var(--text-primary)" }}>
                <strong className="font-serif">{p.name}</strong> — {p.role}.
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section className="mb-8">
        <div className="overline mb-3">Credentials</div>
        <ul className="grid md:grid-cols-2 gap-x-6 gap-y-1.5 text-sm" style={{ color: "var(--text-primary)" }}>
          {certifications.map((c, i) => (
            <li key={i} className="flex gap-2">
              <span className="font-mono text-[10px] pt-1 shrink-0" style={{ color: "var(--accent-strong)" }}>{c.year}</span>
              <span>{c.label}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className="mb-6">
        <div className="overline mb-2">Education</div>
        <div className="text-sm" style={{ color: "var(--text-primary)" }}>
          <strong className="font-serif">B.Tech (Electronics &amp; Telecommunication)</strong> — NIT Raipur, 2006–2010 · CPI 8.55/10.
        </div>
      </section>

      <footer className="mt-10 pt-4 text-xs font-mono uppercase tracking-widest flex justify-between" style={{ color: "var(--text-secondary)", borderTop: "1px solid var(--border-soft)" }}>
        <span>{site.name}</span>
        <span>reconsultx.com</span>
      </footer>
    </div>
  );
}
